import React, { useCallback, useState } from 'react';
import { animated, useSpring } from '@react-spring/web';
import { mergeClassNames } from '../../../utils';
import UparrowIcon from './assets/arrow-up-optical_24.svg';
import AccordionElement from './AccordionElement';
import styles from './index.module.scss';

/**
 * Accordion
 * The `elements` prop is a list of objects with title elements (clickable to
 * expand accordion), the expanding elements, and a corresponding unique key
 * for each object like so:
 *
 * ```javascript
 *  [{
 *    listElement: <div>Clickable list title</div>,
 *    expandingElement: <div>Taller, expandable element</div>,
 *    key: 'this-elems-unique-key',
 *    clickHandler: () => { handleClick() } // optional click handler, overriding internal active accordion elem handler
 *  },{
 *    listElement: <div>Another clickable list title</div>,
 *    expandingElement: <div>Another taller, expandable element</div>,
 *    key: 'another-elems-unique-key',
 *    clickHandler: () => { handleClick() } // optional click handler, overriding internal active accordion elem handler
 *  }]
 * ```
 *
 * This Accordion component will calculate the height of the expanding element
 * to determine the animated height, se pleace note that **margins will not be
 * considered** and using margins on/in the `expandingElement` will result in
 * undesirable results.
 *
 * The active elements can be set from the outside through the `activeElements`
 * prop. Send in an array with keys equal to the initWith array.
 * ! The initWith has no effect if the activeElements is used.
 */
export default function Accordion({
  elements,
  ListElement = ListElemWrapper,
  listElementClassName = null,
  listElementStyle = null,
  title = null,
  className = null,
  style = null,
  multi = false,
  initWith = [], //TODO should not animate w/ init
}) {
  elements = elements ? elements : [];
  const [activeElements, setInnerActiveElements] = useState(initWith);
  const toggleElem = useCallback(
    key => {
      setInnerActiveElements(elms => {
        if (elms.includes(key)) {
          let newElms = [...elms];
          newElms.splice(newElms.indexOf(key), 1);
          return newElms;
        } else if (multi) {
          return [...elms, key];
        } else {
          return [key];
        }
      });
    },
    [multi]
  );

  return (
    <ul className={mergeClassNames([styles.accordion, className])} style={style}>
      {title}
      {elements.map((element, index) => {
        let { listElement, expandingElement, key = index, clickHandler = null } = element;
        let isActive = activeElements.includes(key) || activeElements.includes(element);

        return (
          <AccordionElement
            key={key}
            isVisible={isActive}
            onClick={() => (clickHandler ? clickHandler() : toggleElem(key))}
            expandingElement={expandingElement}
            className={listElementClassName}
            style={listElementStyle}
          >
            <ListElement isActive={isActive}>{listElement}</ListElement>
          </AccordionElement>
        );
      })}
    </ul>
  );
}

function ListElemWrapper({ children, isActive }) {
  let motionProps = useSpring({
    transform: `rotate(${isActive ? 0 : 180}deg)`,
  });

  return (
    <>
      {children}
      <animated.div style={motionProps} className={styles.arrowButton}>
        <UparrowIcon />
      </animated.div>
    </>
  );
}
